import { z } from "zod";
import { clampedLimit } from "./common.schema";

// ADV-923: Customer-registered webhook endpoint schemas.
//
// Each row represents a Hookdeck destination mirrored into Adva's database.
// Customers never reference Hookdeck IDs directly — they always work in terms
// of the Adva `id`. The `hookdeck_destination_id` is exposed read-only so
// operators can correlate with the Hookdeck dashboard but customers cannot
// set it. See the research doc + ADR-0029 for the tenant/destination model.

export const WebhookEndpointSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  name: z.string().describe("Name"),
  url: z.string().url().describe("URL"),
  hookdeck_destination_id: z.string().describe("Hookdeck Destination"),
  event_types: z.array(z.string()).describe("Event Types"),
  entity_types: z.array(z.string()).describe("Entity Types"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});

export type WebhookEndpoint = z.infer<typeof WebhookEndpointSchema>;

/**
 * Create input. `event_types` defaults to `["*"]` (subscribe to all topics)
 * if the caller omits it. `entity_types` is an Adva-side echo filter, not
 * forwarded to Hookdeck — it's recorded so the customer-facing UI can render
 * what they registered. Topic-level matching at Hookdeck is driven by
 * `event_types`.
 */
export const CreateWebhookEndpointSchema = z.object({
  name: z.string().min(1).max(120).describe("Name"),
  url: z.string().url().describe("URL"),
  event_types: z.array(z.string().min(1)).optional().describe("Event Types"),
  entity_types: z.array(z.string().min(1)).optional().describe("Entity Types"),
  is_active: z.boolean().optional().describe("Active"),
});

export type CreateWebhookEndpointInput = z.input<
  typeof CreateWebhookEndpointSchema
>;

export const UpdateWebhookEndpointSchema =
  CreateWebhookEndpointSchema.partial();

export type UpdateWebhookEndpointInput = z.input<
  typeof UpdateWebhookEndpointSchema
>;

// Parse `?is_active=true|false` from query strings literally — Zod's
// `z.coerce.boolean()` would follow JS truthiness and treat the string
// "false" as truthy, silently breaking `?is_active=false` requests.
const QueryBooleanSchema = z.preprocess((v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    if (v === "true") return true;
    if (v === "false") return false;
  }
  return v;
}, z.boolean());

export const WebhookEndpointQuerySchema = z.object({
  is_active: QueryBooleanSchema.optional(),
  limit: clampedLimit(500, 50).optional(),
  offset: z.coerce.number().int().min(0).default(0).optional(),
});

export type WebhookEndpointQuery = z.infer<typeof WebhookEndpointQuerySchema>;

export const WebhookPortalUrlResponseSchema = z.object({
  redirect_url: z.string().url().describe("Redirect URL"),
});

export type WebhookPortalUrlResponse = z.infer<
  typeof WebhookPortalUrlResponseSchema
>;
